<script setup lang="ts">
import { SetQueryParam, QueryParams } from "@/api/client";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";
import { computed, toRefs } from "vue";
import SearchBarSelect from "./SearchBarSelect.vue";
import SearchBarInputIconButton from "./SearchBarInputIconButton.vue";

const props = defineProps<{
  setQueryParam: SetQueryParam;
  queryParams: QueryParams;
  filterHint: {
    label: string;
    name: string;
    type?: string;
    options?: {
      label: string;
      value: string | null | boolean | undefined | number | string[];
    }[];
    addons?: string[];
  };
}>();
const { setQueryParam, filterHint, queryParams } = toRefs(props);

const submit = (
  key: string,
  value: string | null | boolean | undefined | number | string[],
) => {
  setQueryParam.value(key, value);
};

const prevValueOfFilterHint = computed(() => {
  const options = filterHint.value.options || [];
  const currentValue = queryParams.value[filterHint.value.name];
  const currentIndex = options.findIndex((o) => o.value === currentValue);
  if (currentIndex === -1) return null;
  const nextIndex = currentIndex - 1;
  if (nextIndex < 0) return null;
  return options[nextIndex].value;
});

const nextValueOfFilterHint = computed(() => {
  const options = filterHint.value.options || [];
  const currentValue = queryParams.value[filterHint.value.name];
  const currentIndex = options.findIndex((o) => o.value === currentValue);
  if (currentIndex === -1) return null;
  const nextIndex = currentIndex + 1;
  if (nextIndex >= options.length) return null;
  return options[nextIndex].value;
});
</script>

<template>
  <div
    class="flex items-center h-8 pl-2 border rounded border-slate-200 bg-slate-200"
  >
    <label
      class="text-sm font-medium text-slate-800"
      :for="`query-bar-${filterHint.name}`"
    >
      {{ filterHint.label }}:
    </label>
    <input
      v-if="filterHint.type === 'text'"
      :id="`query-bar-${filterHint.name}`"
      :value="queryParams[filterHint.name]"
      :name="filterHint.name"
      class="h-full py-0 pl-1 pr-0 ml-2 text-base border border-transparent bg-slate-100 focus:outline-none"
      @input="
        ($event) =>
          submit(filterHint.name, ($event.target as HTMLInputElement).value)
      "
    />
    <SearchBarSelect
      v-else
      :id="`query-bar-${filterHint.name}`"
      :value="queryParams[filterHint.name]"
      :name="filterHint.name"
      :class="filterHint.addons?.length ? '' : 'rounded-r'"
      :options="filterHint.options"
      @change="submit(filterHint.name, $event)"
    />
    <SearchBarInputIconButton
      v-if="filterHint.addons?.includes('prev') && prevValueOfFilterHint"
      @click="submit(filterHint.name, prevValueOfFilterHint)"
    >
      <ChevronLeftIcon class="w-6 h-6" />
    </SearchBarInputIconButton>
    <SearchBarInputIconButton
      v-if="filterHint.addons?.includes('next') && nextValueOfFilterHint"
      class="rounded-r"
      @click="submit(filterHint.name, nextValueOfFilterHint)"
    >
      <ChevronRightIcon class="w-6 h-6" />
    </SearchBarInputIconButton>
  </div>
</template>
